import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch
} from "@veritrellis/ui";
import { PolicyRuleCard } from "./PolicyRuleCard.js";
import { emptyRule } from "./builderTypes.js";
import { defaultDecisionLabel } from "../../lib/policyPresentation.js";
import type { PlanFeatures } from "../../hooks/useWorkspaceBilling.js";
import type { BuilderRule, BuilderState } from "./builderTypes.js";

type PolicyRuleLadderProps = {
  state: BuilderState;
  onChange: (next: BuilderState) => void;
  workspaceId: string;
  environment: "sandbox" | "production";
  planFeatures: PlanFeatures;
  billingLoaded?: boolean;
  disabled?: boolean;
  matchedRuleIndex?: number | null;
};

const defaultOptions = [
  { value: "allow", label: "Allow" },
  { value: "deny", label: "Deny" },
  { value: "require_approval", label: "Require approval" }
] as const;

export const PolicyRuleLadder = ({
  state,
  onChange,
  workspaceId,
  environment,
  planFeatures,
  billingLoaded = true,
  disabled = false,
  matchedRuleIndex = null
}: PolicyRuleLadderProps) => {
  const canAddRule = planFeatures.escalationRules || state.rules.length === 0 || !billingLoaded;
  const updateRule = (i: number, updated: BuilderRule) => {
    onChange({ ...state, rules: state.rules.map((r, ri) => (ri === i ? updated : r)) });
  };

  const removeRule = (i: number) => {
    onChange({ ...state, rules: state.rules.filter((_, ri) => ri !== i) });
  };

  const moveRule = (i: number, direction: "up" | "down") => {
    const j = direction === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= state.rules.length) {
      return;
    }
    const rules = [...state.rules];
    [rules[i], rules[j]] = [rules[j]!, rules[i]!];
    onChange({ ...state, rules });
  };

  return (
    <div className="admin-policy-ladder">
      <div className="flex items-center gap-2">
        <Switch
          id="auto-allow-low-risk"
          disabled={disabled}
          checked={state.autoAllowLowRisk}
          onCheckedChange={(checked) => onChange({ ...state, autoAllowLowRisk: checked === true })}
        />
        <div>
          <Label htmlFor="auto-allow-low-risk" className="cursor-pointer">
            Auto-allow low risk
          </Label>
          <p style={{ margin: 0, fontSize: 12, color: "var(--muted-foreground)" }}>
            Low risk_class requests bypass rules and are allowed immediately
          </p>
        </div>
      </div>

      <div className="admin-policy-ladder-rules">
        {state.rules.map((rule, i) => (
          <PolicyRuleCard
            key={rule.id}
            rule={rule}
            index={i}
            total={state.rules.length}
            workspaceId={workspaceId}
            environment={environment}
            planFeatures={planFeatures}
            billingLoaded={billingLoaded}
            onChange={(updated) => updateRule(i, updated)}
            onRemove={() => removeRule(i)}
            onMoveUp={() => moveRule(i, "up")}
            onMoveDown={() => moveRule(i, "down")}
            disabled={disabled}
            matched={matchedRuleIndex === i}
          />
        ))}
      </div>

      <div className="flex flex-col items-start gap-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={disabled || !canAddRule}
          onClick={() => {
            onChange({ ...state, rules: [...state.rules, emptyRule(crypto.randomUUID())] });
          }}
        >
          + Add rule
        </Button>
        {billingLoaded && !planFeatures.escalationRules && state.rules.length >= 1 ? (
          <p style={{ margin: 0, fontSize: 12, color: "var(--muted-foreground)" }}>
            Multi-rule ladders require Pro or Enterprise.
          </p>
        ) : null}
      </div>

      <article className="admin-policy-default">
        <p className="admin-policy-default-label">Default (no rule matched)</p>
        <Select
          value={state.defaultDecision}
          onValueChange={(v) => {
            onChange({ ...state, defaultDecision: v as BuilderState["defaultDecision"] });
          }}
          disabled={disabled}
        >
          <SelectTrigger className="h-8" style={{ maxWidth: 220 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {defaultOptions.map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="admin-policy-default-hint">
          Otherwise → {defaultDecisionLabel(state.defaultDecision)}
        </p>
      </article>
    </div>
  );
};
